// Dialog "Personalização Pizzas" — visão consolidada de tamanhos + bordas de
// TODAS as pizzas do tenant + ações em massa (esgotar/reativar + edição de preço).
// Layout em tabs: Tamanhos | Bordas. Cada item tem:
//   - Toggle switch "ativo/esgotado"
//   - Input de preço editável (bulk update no blur)
//   - Bordas: matriz preço-por-tamanho em grid
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pizza, Loader2, Sparkles, Plus, X, Pencil, Check, Users } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { PriceInput } from "./PizzaStepEditor";

const fmtR$ = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const parseSteps = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") { try { return JSON.parse(raw); } catch { return []; } }
  return [];
};
const norm = (s) => String(s || "").trim().toLowerCase();

function getAuthHeader() {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

export default function PizzaPersonalizationDialog({ open, onClose, products = [], onChanged }) {
  const [pendingKey, setPendingKey] = useState(null);
  // Form inline pra adicionar novo tamanho
  const [showAddSize, setShowAddSize] = useState(false);
  const [newSize, setNewSize] = useState({ name: "", slices: 8, max_flavors: 2, price: 0 });
  // Form inline pra adicionar nova borda
  const [showAddBorder, setShowAddBorder] = useState(false);
  const [newBorder, setNewBorder] = useState({ name: "", pricesByName: {} });
  // Rename inline: chave "{kind}:{currentName}" → new name (string)
  const [renaming, setRenaming] = useState(null); // {kind, oldName, value}
  // Popover "quais pizzas" — cache por (kind, name) → array de pizzas
  const [usageCache, setUsageCache] = useState({}); // {"kind:name": {loading, pizzas}}

  // ---- Agregação ----
  const summary = useMemo(() => {
    // Filtra pizzas: só product_type='pizza' E não-arquivadas. Arquivadas
    // são sub-painel separado no admin — incluí-las aqui inflaria a contagem
    // ("em N pizzas") e poluiria o popover "quais pizzas?".
    const pizzas = (products || []).filter(p => (
      p.product_type === "pizza" && !p.is_archived
    ));
    const sizesByName = new Map();
    const bordersByName = new Map();
    // Nomes de tamanhos ÚNICOS no tenant (pra colunas da matriz de bordas)
    const allSizeNames = new Map(); // norm → displayName
    let pizzasCount = 0;
    let pizzasWithoutSteps = 0;

    for (const p of pizzas) {
      const steps = parseSteps(p.order_steps);
      if (!steps.length) { pizzasWithoutSteps++; continue; }
      pizzasCount++;

      const sizeStep = steps.find(s => s?.kind === "pizza_size");
      const flavorStep = steps.find(s => s?.kind === "pizza_flavor");
      const borderStep = steps.find(s => s?.kind === "pizza_border");

      const sizeIdToName = {};
      for (const sz of (sizeStep?.items || [])) {
        if (sz.id && sz.name) {
          sizeIdToName[sz.id] = sz.name;
          allSizeNames.set(norm(sz.name), sz.name);
        }
      }

      const ownPricesBySize = (() => {
        const own = (flavorStep?.items || []).find(it => it.product_id === p.id);
        return own?.prices_by_size || {};
      })();

      // TAMANHOS
      for (const sz of (sizeStep?.items || [])) {
        const key = norm(sz.name);
        if (!key) continue;
        if (!sizesByName.has(key)) {
          sizesByName.set(key, {
            name: sz.name, pizzaCount: 0,
            slicesSet: new Set(), maxFlavorsSet: new Set(),
            prices: [], anyAvailable: false,
          });
        }
        const agg = sizesByName.get(key);
        agg.pizzaCount += 1;
        if (sz.slices != null) agg.slicesSet.add(sz.slices);
        if (sz.max_flavors != null) agg.maxFlavorsSet.add(sz.max_flavors);
        if (sz.available !== false) agg.anyAvailable = true;
        const price = Number(ownPricesBySize[sz.id] || 0);
        if (price > 0) agg.prices.push(price);
      }

      // BORDAS
      for (const bd of (borderStep?.items || [])) {
        const key = norm(bd.name);
        if (!key) continue;
        if (!bordersByName.has(key)) {
          bordersByName.set(key, {
            name: bd.name, pizzaCount: 0,
            pricesByName: {}, anyAvailable: false,
          });
        }
        const agg = bordersByName.get(key);
        agg.pizzaCount += 1;
        if (bd.available !== false) agg.anyAvailable = true;
        const bp = bd.prices_by_size || {};
        for (const [sid, price] of Object.entries(bp)) {
          const sName = sizeIdToName[sid];
          if (!sName) continue;
          const sKey = norm(sName);
          if (!agg.pricesByName[sKey]) {
            agg.pricesByName[sKey] = { displayName: sName, prices: [] };
          }
          const p = Number(price || 0);
          if (p > 0) agg.pricesByName[sKey].prices.push(p);
        }
      }
    }

    const sizeColumns = Array.from(allSizeNames.entries())
      .map(([key, displayName]) => ({ key, displayName }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    const finalizeSizes = (m) => Array.from(m.values())
      .map(agg => ({
        ...agg,
        minPrice: agg.prices.length ? Math.min(...agg.prices) : 0,
        maxPrice: agg.prices.length ? Math.max(...agg.prices) : 0,
        slicesList: Array.from(agg.slicesSet || []).sort((a, b) => a - b),
        maxFlavorsList: Array.from(agg.maxFlavorsSet || []).sort((a, b) => a - b),
      }))
      .sort((a, b) => b.pizzaCount - a.pizzaCount);

    const finalizeBorders = (m) => Array.from(m.values())
      .map(agg => ({
        ...agg,
        // Map sKey → { displayName, price (min), maxPrice, divergent (bool) }
        pricesPerSize: Object.fromEntries(
          Object.entries(agg.pricesByName).map(([sKey, v]) => {
            const price = v.prices.length ? Math.min(...v.prices) : 0;
            const maxPrice = v.prices.length ? Math.max(...v.prices) : 0;
            return [sKey, { displayName: v.displayName, price, maxPrice, divergent: price !== maxPrice }];
          }),
        ),
      }))
      .sort((a, b) => b.pizzaCount - a.pizzaCount);

    return {
      pizzasCount, pizzasWithoutSteps,
      sizeColumns,
      sizes: finalizeSizes(sizesByName),
      borders: finalizeBorders(bordersByName),
    };
  }, [products]);

  const esgotadosCount = useMemo(() => {
    let n = 0;
    for (const s of summary.sizes) if (!s.anyAvailable) n++;
    for (const b of summary.borders) if (!b.anyAvailable) n++;
    return n;
  }, [summary]);

  // bulkSetPrice removido em v1.5.222 — preços editáveis voltaram a ser por pizza
  // individual (na aba Personalização do produto). Aqui só mostra leitura.

  const submitAddSize = async () => {
    const name = (newSize.name || "").trim();
    if (!name) { toast.error("Informe o nome do tamanho"); return; }
    setPendingKey("add:size");
    try {
      const res = await axios.post(`${API}/pizza/bulk/size`, {
        name, slices: Number(newSize.slices) || null,
        max_flavors: Number(newSize.max_flavors) || 1,
        price: Number(newSize.price) || 0,
      }, getAuthHeader());
      const n = res.data?.added_pizzas || 0;
      toast.success(`"${name}" criado em ${n} pizza${n === 1 ? "" : "s"}`);
      setShowAddSize(false);
      setNewSize({ name: "", slices: 8, max_flavors: 2, price: 0 });
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(`Erro: ${e?.response?.data?.detail || e?.message || "desconhecido"}`);
    } finally {
      setPendingKey(null);
    }
  };

  const submitAddBorder = async () => {
    const name = (newBorder.name || "").trim();
    if (!name) { toast.error("Informe o nome da borda"); return; }
    setPendingKey("add:border");
    try {
      const res = await axios.post(`${API}/pizza/bulk/border`, {
        name,
        prices_by_size_name: newBorder.pricesByName || {},
      }, getAuthHeader());
      const n = res.data?.added_pizzas || 0;
      toast.success(`Borda "${name}" criada em ${n} pizza${n === 1 ? "" : "s"}`);
      setShowAddBorder(false);
      setNewBorder({ name: "", pricesByName: {} });
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(`Erro: ${e?.response?.data?.detail || e?.message || "desconhecido"}`);
    } finally {
      setPendingKey(null);
    }
  };

  const submitRename = async () => {
    if (!renaming) return;
    const { kind, oldName, value } = renaming;
    const newName = (value || "").trim();
    if (!newName || newName === oldName) { setRenaming(null); return; }
    setPendingKey(`rename:${kind}:${oldName}`);
    try {
      const res = await axios.put(`${API}/pizza/bulk/rename`, {
        kind, old_name: oldName, new_name: newName,
      }, getAuthHeader());
      const n = res.data?.renamed_pizzas || 0;
      const sk = res.data?.skipped_pizzas || 0;
      if (n > 0) {
        toast.success(`"${oldName}" → "${newName}" em ${n} pizza${n === 1 ? "" : "s"}${sk ? ` (${sk} puladas)` : ""}`);
      } else if (sk > 0) {
        toast.error(`"${newName}" já existe nas pizzas — pulado pra evitar conflito`);
      } else {
        toast.info("Nada pra renomear");
      }
      if (onChanged) onChanged();
      setRenaming(null);
    } catch (e) {
      toast.error(`Erro: ${e?.response?.data?.detail || e?.message || "desconhecido"}`);
    } finally {
      setPendingKey(null);
    }
  };

  const loadUsage = async (kind, name) => {
    const key = `${kind}:${name}`;
    if (usageCache[key]?.pizzas || usageCache[key]?.loading) return;
    setUsageCache(prev => ({ ...prev, [key]: { loading: true, pizzas: null } }));
    try {
      const res = await axios.get(`${API}/pizza/items-usage`, {
        params: { kind, name }, ...getAuthHeader(),
      });
      setUsageCache(prev => ({ ...prev, [key]: { loading: false, pizzas: res.data?.pizzas || [] } }));
    } catch (e) {
      setUsageCache(prev => ({ ...prev, [key]: { loading: false, pizzas: [], error: true } }));
    }
  };

  const toggleAvailability = async (kind, name, currentlyAvailable) => {
    const key = `avail:${kind}:${name}`;
    if (pendingKey) return;
    setPendingKey(key);
    try {
      const res = await axios.put(
        `${API}/pizza/bulk/availability`,
        { kind, name, available: !currentlyAvailable },
        getAuthHeader(),
      );
      toast.success(`${name} ${currentlyAvailable ? "esgotado" : "reativado"} em ${res.data?.updated_pizzas || 0} pizza(s)`);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(`Erro: ${e?.response?.data?.detail || e?.message || "desconhecido"}`);
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
        {/* ===== HEADER em gradient ===== */}
        <DialogHeader className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-br from-orange-500/10 via-amber-500/5 to-transparent border-b text-left">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Pizza className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold">Personalização Pizzas</p>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                <span className="text-xs text-muted-foreground">
                  <strong className="text-foreground">{summary.pizzasCount}</strong> pizza{summary.pizzasCount === 1 ? "" : "s"} ativa{summary.pizzasCount === 1 ? "" : "s"}
                </span>
                {summary.pizzasWithoutSteps > 0 && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
                    {summary.pizzasWithoutSteps} sem config
                  </span>
                )}
                {esgotadosCount > 0 && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-600 dark:text-red-400">
                    {esgotadosCount} esgotado{esgotadosCount === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* ===== TABS ===== */}
        <Tabs defaultValue="sizes" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-4 sm:mx-6 mt-4 grid w-auto grid-cols-2 bg-muted/60">
            <TabsTrigger value="sizes" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Pizza className="w-3.5 h-3.5 mr-1.5" />
              Tamanhos ({summary.sizes.length})
            </TabsTrigger>
            <TabsTrigger value="borders" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <span className="w-3.5 h-3.5 mr-1.5 rounded-full border-[3px] border-current inline-block" />
              Bordas ({summary.borders.length})
            </TabsTrigger>
          </TabsList>

          {/* ===== TAB TAMANHOS ===== */}
          <TabsContent value="sizes" className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 mt-0 data-[state=inactive]:hidden">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
              <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                <Sparkles className="w-3 h-3 mt-0.5 text-orange-500 flex-shrink-0" />
                Use o switch pra esgotar/reativar. O preço aparece em faixa quando varia entre pizzas.
              </p>
              {!showAddSize && (
                <Button type="button" size="sm" onClick={() => setShowAddSize(true)} className="bg-orange-500 hover:bg-orange-600 text-white flex-shrink-0 self-start sm:self-auto">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar tamanho
                </Button>
              )}
            </div>

            {/* Form inline ADICIONAR TAMANHO */}
            {showAddSize && (
              <div className="mb-3 rounded-xl border-2 border-orange-500/40 bg-orange-500/[0.05] p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-orange-700 dark:text-orange-300">Novo tamanho</h4>
                  <button type="button" onClick={() => setShowAddSize(false)} className="p-1 rounded hover:bg-orange-500/10 text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Nome</label>
                    <Input
                      value={newSize.name}
                      onChange={(e) => setNewSize(s => ({ ...s, name: e.target.value }))}
                      placeholder="Grande, Média..."
                      className="h-9 mt-0.5"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Fatias</label>
                    <Input
                      type="number" min="1"
                      value={newSize.slices ?? ""}
                      onChange={(e) => setNewSize(s => ({ ...s, slices: e.target.value ? parseInt(e.target.value, 10) : null }))}
                      placeholder="8"
                      className="h-9 mt-0.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Máx sabores</label>
                    <Input
                      type="number" min="1"
                      value={newSize.max_flavors}
                      onChange={(e) => setNewSize(s => ({ ...s, max_flavors: parseInt(e.target.value, 10) || 1 }))}
                      placeholder="2"
                      className="h-9 mt-0.5"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Preço</label>
                    <div className="flex items-center h-9 mt-0.5 rounded-md border border-input bg-background overflow-hidden">
                      <span className="text-xs font-bold text-muted-foreground px-2">R$</span>
                      <PriceInput
                        value={newSize.price}
                        onChange={(p) => setNewSize(s => ({ ...s, price: p }))}
                        className="h-9 flex-1 min-w-0 text-sm text-right tabular-nums font-semibold border-0 px-1.5 bg-transparent"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
                  <Button type="button" variant="ghost" onClick={() => setShowAddSize(false)} disabled={pendingKey === "add:size"}>Cancelar</Button>
                  <Button type="button" onClick={submitAddSize} disabled={pendingKey === "add:size" || !newSize.name.trim()} className="bg-orange-500 hover:bg-orange-600 text-white">
                    {pendingKey === "add:size" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-1" />}
                    Adicionar em todas as pizzas
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Preço será aplicado no sabor próprio de cada pizza. Pizzas que já têm tamanho com esse nome são puladas.
                </p>
              </div>
            )}

            {summary.sizes.length === 0 ? (
              <EmptyState text="Nenhum tamanho configurado em nenhuma pizza" />
            ) : (
              <div className="space-y-2">
                {summary.sizes.map(sz => {
                  const isPendingAvail = pendingKey === `avail:size:${sz.name}`;
                  const esgotado = !sz.anyAvailable;
                  const divergent = sz.prices.length > 0 && sz.minPrice !== sz.maxPrice;
                  return (
                    <div key={sz.name} className={`group relative rounded-xl border-2 transition-all overflow-hidden ${esgotado ? "border-red-500/30 bg-red-500/[0.03]" : "border-border hover:border-orange-300 bg-card"}`}>
                      <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 p-3 sm:p-4">
                        {/* Ícone */}
                        <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors ${esgotado ? "bg-red-500/10 text-red-500" : "bg-gradient-to-br from-orange-500/15 to-amber-500/10 text-orange-500"}`}>
                          <Pizza className="w-6 h-6" />
                        </div>
                        {/* Info — nome editável + contador clicável */}
                        <div className="flex-1 min-w-0">
                          <ItemHeader
                            kind="size" name={sz.name} pizzaCount={sz.pizzaCount}
                            esgotado={esgotado} renaming={renaming} setRenaming={setRenaming}
                            submitRename={submitRename} pendingKey={pendingKey}
                            usage={usageCache} loadUsage={loadUsage}
                          />
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {sz.slicesList.length > 0 && `${sz.slicesList.join("/")} fatia${sz.slicesList[0] === 1 ? "" : "s"}`}
                            {sz.slicesList.length > 0 && sz.maxFlavorsList.length > 0 && " · "}
                            {sz.maxFlavorsList.length > 0 && `até ${sz.maxFlavorsList.join("/")} sabor${sz.maxFlavorsList[0] === 1 ? "" : "es"}`}
                          </p>
                        </div>
                        {/* Preço (leitura): menor–maior se divergente, único se igual.
                            No celular vira a linha de baixo (rótulo à esquerda, valor à direita). */}
                        <div className="order-last sm:order-none w-full sm:w-auto flex flex-row sm:flex-col items-center sm:items-end justify-between gap-1 flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-border/50">
                          <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                            {divergent ? "Preço (faixa)" : "Preço"}
                          </label>
                          {sz.prices.length > 0 ? (
                            <p className={`text-base font-bold tabular-nums ${esgotado ? "text-muted-foreground line-through" : "text-orange-600 dark:text-orange-400"}`}>
                              {divergent
                                ? `${fmtR$(sz.minPrice)} – ${fmtR$(sz.maxPrice)}`
                                : fmtR$(sz.minPrice)}
                            </p>
                          ) : (
                            <p className="text-[11px] text-muted-foreground italic">sem preços</p>
                          )}
                        </div>
                        {/* Switch ativo/esgotado */}
                        <div className="flex flex-col items-center gap-1 flex-shrink-0 ml-1">
                          <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                            {esgotado ? "Esgotado" : "Ativo"}
                          </label>
                          {isPendingAvail ? (
                            <div className="h-6 w-11 flex items-center justify-center">
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            </div>
                          ) : (
                            <Switch
                              checked={!esgotado}
                              onCheckedChange={() => toggleAvailability("size", sz.name, !esgotado)}
                              className="data-[state=checked]:bg-green-500"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ===== TAB BORDAS ===== */}
          <TabsContent value="borders" className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 mt-0 data-[state=inactive]:hidden">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
              <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                <Sparkles className="w-3 h-3 mt-0.5 text-orange-500 flex-shrink-0" />
                Use o switch pra esgotar/reativar. Preços mostram faixa quando variam entre pizzas.
              </p>
              {!showAddBorder && (
                <Button type="button" size="sm" onClick={() => setShowAddBorder(true)} className="bg-orange-500 hover:bg-orange-600 text-white flex-shrink-0 self-start sm:self-auto">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar borda
                </Button>
              )}
            </div>

            {/* Form inline ADICIONAR BORDA */}
            {showAddBorder && (
              <div className="mb-3 rounded-xl border-2 border-orange-500/40 bg-orange-500/[0.05] p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-orange-700 dark:text-orange-300">Nova borda</h4>
                  <button type="button" onClick={() => setShowAddBorder(false)} className="p-1 rounded hover:bg-orange-500/10 text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mb-3">
                  <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Nome</label>
                  <Input
                    value={newBorder.name}
                    onChange={(e) => setNewBorder(s => ({ ...s, name: e.target.value }))}
                    placeholder="Catupiry, Cheddar..."
                    className="h-9 mt-0.5"
                    autoFocus
                  />
                </div>
                {summary.sizeColumns.length === 0 ? (
                  <p className="text-[11px] text-amber-700 dark:text-amber-300 italic">
                    Cadastre tamanhos primeiro pra definir preço por tamanho.
                  </p>
                ) : (
                  <>
                    <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Preço por tamanho</label>
                    <div className="grid gap-2 mt-1" style={{ gridTemplateColumns: `repeat(${Math.min(summary.sizeColumns.length, 3)}, minmax(0, 1fr))` }}>
                      {summary.sizeColumns.map(col => {
                        const value = newBorder.pricesByName[col.key] || 0;
                        return (
                          <div key={col.key} className="flex flex-col rounded-lg border border-input bg-background px-2 py-1.5">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">
                              {col.displayName}
                            </span>
                            <div className="flex items-center mt-0.5">
                              <span className="text-xs font-bold text-muted-foreground">+R$</span>
                              <PriceInput
                                value={value}
                                onChange={(p) => setNewBorder(s => ({ ...s, pricesByName: { ...s.pricesByName, [col.key]: p } }))}
                                className="h-7 flex-1 min-w-0 text-sm text-right tabular-nums font-bold border-0 px-1 bg-transparent"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-3">
                  <Button type="button" variant="ghost" onClick={() => setShowAddBorder(false)} disabled={pendingKey === "add:border"}>Cancelar</Button>
                  <Button type="button" onClick={submitAddBorder} disabled={pendingKey === "add:border" || !newBorder.name.trim()} className="bg-orange-500 hover:bg-orange-600 text-white">
                    {pendingKey === "add:border" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-1" />}
                    Adicionar em todas as pizzas
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Pizzas que já têm borda com esse nome são puladas.
                </p>
              </div>
            )}

            {summary.borders.length === 0 ? (
              <EmptyState text="Nenhuma borda configurada" />
            ) : (
              <div className="space-y-2">
                {summary.borders.map(bd => {
                  const isPendingAvail = pendingKey === `avail:border:${bd.name}`;
                  const esgotado = !bd.anyAvailable;
                  return (
                    <div key={bd.name} className={`group rounded-xl border-2 transition-all overflow-hidden ${esgotado ? "border-red-500/30 bg-red-500/[0.03]" : "border-border hover:border-orange-300 bg-card"}`}>
                      {/* HEADER da borda */}
                      <div className="flex items-center gap-3 px-3 sm:px-4 py-3 border-b border-border/50">
                        <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${esgotado ? "bg-red-500/10" : "bg-gradient-to-br from-orange-500/15 to-amber-500/10"}`}>
                          <span className={`w-5 h-5 rounded-full border-[4px] ${esgotado ? "border-red-500/40" : "border-orange-500/70"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <ItemHeader
                            kind="border" name={bd.name} pizzaCount={bd.pizzaCount}
                            esgotado={esgotado} renaming={renaming} setRenaming={setRenaming}
                            submitRename={submitRename} pendingKey={pendingKey}
                            usage={usageCache} loadUsage={loadUsage}
                          />
                        </div>
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                          <label className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                            {esgotado ? "Esgotado" : "Ativo"}
                          </label>
                          {isPendingAvail ? (
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          ) : (
                            <Switch
                              checked={!esgotado}
                              onCheckedChange={() => toggleAvailability("border", bd.name, !esgotado)}
                              className="data-[state=checked]:bg-green-500"
                            />
                          )}
                        </div>
                      </div>
                      {/* GRID de preço por tamanho — apenas LEITURA (faixa min–max ou único) */}
                      <div className="p-3 bg-muted/20">
                        {summary.sizeColumns.length === 0 ? (
                          <p className="text-[11px] text-muted-foreground italic text-center py-2">
                            Cadastre tamanhos primeiro
                          </p>
                        ) : (
                          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(summary.sizeColumns.length, 3)}, minmax(0, 1fr))` }}>
                            {summary.sizeColumns.map(col => {
                              const pp = bd.pricesPerSize[col.key];
                              const value = pp?.price || 0;
                              const max = pp?.maxPrice || value;
                              const divergent = pp?.divergent;
                              const isFree = !value;
                              return (
                                <div key={col.key} className={`flex flex-col rounded-lg border px-2 py-1.5 ${isFree ? "border-border/60 bg-background" : "border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20"}`}>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">
                                    {col.displayName}
                                  </span>
                                  <p className={`text-sm tabular-nums font-bold mt-0.5 ${isFree ? "text-muted-foreground italic" : "text-orange-700 dark:text-orange-400"}`}>
                                    {isFree
                                      ? "grátis"
                                      : divergent
                                        ? `+${fmtR$(value)}–${fmtR$(max)}`
                                        : `+${fmtR$(value)}`}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ text }) {
  return (
    <div className="text-center py-12 border-2 border-dashed border-border rounded-xl">
      <Pizza className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

// ItemHeader: nome editável + contador clicável (popover com lista de pizzas).
// Reusado em Tamanhos e Bordas pra evitar duplicação.
function ItemHeader({ kind, name, pizzaCount, esgotado, renaming, setRenaming, submitRename, pendingKey, usage, loadUsage }) {
  const isEditing = renaming?.kind === kind && renaming?.oldName === name;
  const isRenamingPending = pendingKey === `rename:${kind}:${name}`;
  const usageKey = `${kind}:${name}`;
  const usageState = usage[usageKey];

  return (
    <div className="flex items-baseline gap-2 flex-wrap">
      {/* Nome — clique no ícone lápis pra editar */}
      {isEditing ? (
        <div className="flex items-center gap-1">
          <Input
            value={renaming.value}
            onChange={(e) => setRenaming(r => ({ ...r, value: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenaming(null); }}
            className="h-7 w-32 text-base font-bold"
            autoFocus
            disabled={isRenamingPending}
          />
          <Button type="button" size="icon" variant="ghost" onClick={submitRename} disabled={isRenamingPending} className="h-7 w-7 text-green-600 hover:text-green-700">
            {isRenamingPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
          <Button type="button" size="icon" variant="ghost" onClick={() => setRenaming(null)} disabled={isRenamingPending} className="h-7 w-7 text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <>
          <h4 className={`text-base font-bold ${esgotado ? "line-through text-muted-foreground" : "text-foreground"}`}>{name}</h4>
          <button
            type="button"
            onClick={() => setRenaming({ kind, oldName: name, value: name })}
            className="p-2 sm:p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground opacity-50 hover:opacity-100"
            title={`Renomear ${kind === "size" ? "tamanho" : "borda"} em todas as pizzas`}
          >
            <Pencil className="w-3 h-3" />
          </button>
        </>
      )}
      {/* Contador clicável — abre popover com lista de pizzas */}
      <Popover onOpenChange={(o) => { if (o) loadUsage(kind, name); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="text-[11px] text-muted-foreground hover:text-orange-500 inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-orange-500/10 transition-colors whitespace-nowrap"
            title="Clique pra ver quais pizzas"
          >
            <Users className="w-3 h-3" />
            em <strong className="underline decoration-dotted">{pizzaCount} pizza{pizzaCount === 1 ? "" : "s"}</strong>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="px-3 py-2 border-b">
            <p className="text-xs font-bold text-foreground">
              "{name}" presente em {pizzaCount} pizza{pizzaCount === 1 ? "" : "s"}
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {usageState?.loading ? (
              <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground mx-auto" /></div>
            ) : usageState?.pizzas?.length > 0 ? (
              <ul className="divide-y">
                {usageState.pizzas.map(p => (
                  <li key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40">
                    <div className="w-7 h-7 rounded bg-muted overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {p.photo_url ? (
                        <img src={p.photo_url.startsWith("http") || p.photo_url.startsWith("data:") ? p.photo_url : `/api${p.photo_url}`} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <Pizza className="w-3 h-3 text-muted-foreground/40" />
                      )}
                    </div>
                    <span className={`text-xs flex-1 truncate ${!p.available ? "line-through text-muted-foreground" : ""}`}>{p.name}</span>
                    {!p.available && (
                      <span className="text-[8px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400">Esg</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">Nenhuma pizza encontrada</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
